import Image from 'next/image'
import Link from 'next/link'
import styled from 'styled-components'

import { getFullName, ICircunscripcion } from '@/app/2023/consejo/types/circunscripciones'
import { IColumn, TSorterFunction } from '@/app/components/Table/types'
import { colors, spaces } from '@/config/global-parameters'
import { normalNumberFormatter, percentFormatter } from '@/helpers/formatters'

import ListVotesItem from './ListVotesItem'

export interface IResultadoCircunscripcion {
  id: string
  name: string
  circunscripcion: ICircunscripcion
  votos: {
    [key: string]: number
    totales: number
    0: number
    1: number
    2: number
    3: number
    4: number
    6: number
  }
}

const variableListSorter = (listNumber: number, selectedUnit: string, isInd?: boolean) => {
  const listSorter: TSorterFunction = ({ rows, status }) => {
    const sortingFunc = (a: IResultadoCircunscripcion, b: IResultadoCircunscripcion) => {
      const priorityA = isInd && ['11', '14', '15'].includes(a.id) ? 1 : 0
      const priorityB = isInd && ['11', '14', '15'].includes(b.id) ? 1 : 0
      if (priorityA && !priorityB) return -1
      if (!priorityA && priorityB) return 1
      return status === 'asc'
        ? a.votos[listNumber] - b.votos[listNumber]
        : b.votos[listNumber] - a.votos[listNumber]
    }
    const sortingPercentageFunc = (a: IResultadoCircunscripcion, b: IResultadoCircunscripcion) => {
      const priorityA = isInd && ['11', '14', '15'].includes(a.id) ? 1 : 0
      const priorityB = isInd && ['11', '14', '15'].includes(b.id) ? 1 : 0
      if (priorityA && !priorityB) return -1
      if (!priorityA && priorityB) return 1
      return status === 'asc'
        ? a.votos[listNumber] / a.votos.totales - b.votos[listNumber] / b.votos.totales
        : b.votos[listNumber] / b.votos.totales - a.votos[listNumber] / a.votos.totales
    }
    const sortedRows = selectedUnit === 'porcentaje' ? rows.sort(sortingPercentageFunc) : rows.sort(sortingFunc)
    return sortedRows
  }

  return listSorter
}

const getTableColumns = (selectedUnit: string) => {
  const tableColumns: IColumn[] = [
    {
      align: 'flex-start',
      canSort: true,
      dataIndex: 'name',
      key: 'region',
      render: ({ circunscripcion, id }) => (
        <RegionWrapper>
          <ImageWrapper>
            <Image
              alt={circunscripcion.name}
              height={40}
              src={`/images/territories/${circunscripcion.svg}`}
              width={40}
            />
          </ImageWrapper>
          <CircumscriptionText>
            <Link href={`/2023/consejo/circunscripciones/${id}`}>{getFullName(id)}</Link>
          </CircumscriptionText>
        </RegionWrapper>
      ),
      title: 'CIRCUNSCRIPCIONES',
    },
    {
      align: 'center',
      canSort: true,
      dataIndex: 'UPCh',
      key: 'UPCh',
      render: ({ votos }) => (
        <VoteWrapper>
          <VoteOption1>
            <ListText>
              {selectedUnit === 'porcentaje'
                ? percentFormatter.format(votos.totales ? Number(votos[3] / votos.totales) || 0 : 0)
                : normalNumberFormatter.format(Number(votos[3]) || 0)}
            </ListText>
          </VoteOption1>
          <VoteOption2>
            <ListVotesItem
              circumscriptionVotes={votos.totales}
              list="3"
              listVotes={votos[3]}
            />
          </VoteOption2>
        </VoteWrapper>
      ),
      sorter: variableListSorter(3, selectedUnit),
      title: (
        <>
          UNIDAD
          <br />
          PARA CHILE
        </>
      ),
    },
    {
      align: 'center',
      canSort: true,
      dataIndex: 'TPCh',
      key: 'TPCh',
      render: ({ votos }) => (
        <VoteWrapper>
          <VoteOption1>
            <ListText>
              {selectedUnit === 'porcentaje'
                ? percentFormatter.format(votos.totales ? Number(votos[1] / votos.totales) || 0 : 0)
                : normalNumberFormatter.format(Number(votos[1]) || 0)}
            </ListText>
          </VoteOption1>
          <VoteOption2>
            <ListVotesItem
              circumscriptionVotes={votos.totales}
              list="1"
              listVotes={votos[1]}
            />
          </VoteOption2>
        </VoteWrapper>
      ),
      sorter: variableListSorter(1, selectedUnit),
      title: (
        <>
          TODO
          <br />
          POR CHILE
        </>
      ),
    },
    {
      align: 'center',
      canSort: true,
      dataIndex: 'IND',
      key: 'IND',
      render: ({ id, votos }) => {
        if (['11', '14', '15'].includes(id)) {
          return (
            <VoteWrapper>
              <VoteOption1>
                <ListText>
                  {selectedUnit === 'porcentaje'
                    ? percentFormatter.format(
                      votos.totales ? Number(votos[6] / votos.totales) || 0 : 0
                    )
                    : normalNumberFormatter.format(Number(votos[6]) || 0)}
                </ListText>
              </VoteOption1>
              <VoteOption2>
                <ListVotesItem
                  circumscriptionVotes={votos.totales}
                  list="6"
                  listVotes={votos[6]}
                />
              </VoteOption2>
            </VoteWrapper>
          )
        }
        return <ListText style={{ color: colors.colorGrey12 }}>--</ListText>
      },
      sorter: variableListSorter(6, selectedUnit, true),
      title: 'INDEP.',
    },
    {
      align: 'center',
      canSort: true,
      dataIndex: 'PDG',
      key: 'PDG',
      render: ({ votos }) => (
        <VoteWrapper>
          <VoteOption1>
            <ListText>
              {selectedUnit === 'porcentaje'
                ? percentFormatter.format(votos.totales ? Number(votos[0] / votos.totales) || 0 : 0)
                : normalNumberFormatter.format(Number(votos[0] || 0))}
            </ListText>
          </VoteOption1>
          <VoteOption2>
            <ListVotesItem
              circumscriptionVotes={votos.totales}
              list="0"
              listVotes={votos[0]}
            />
          </VoteOption2>
        </VoteWrapper>
      ),
      sorter: variableListSorter(0, selectedUnit),
      title: (
        <>
          PARTIDO DE
          <br />
          LA GENTE
        </>
      ),
    },
    {
      align: 'center',
      canSort: true,
      dataIndex: 'ChS',
      key: 'ChS',
      render: ({ votos }) => (
        <VoteWrapper>
          <VoteOption1>
            <ListText>
              {selectedUnit === 'porcentaje'
                ? percentFormatter.format(votos.totales ? Number(votos[4] / votos.totales) || 0 : 0)
                : normalNumberFormatter.format(Number(votos[4]) || 0)}
            </ListText>
          </VoteOption1>
          <VoteOption2>
            <ListVotesItem
              circumscriptionVotes={votos.totales}
              list="4"
              listVotes={votos[4]}
            />
          </VoteOption2>
        </VoteWrapper>
      ),
      sorter: variableListSorter(4, selectedUnit),
      title: (
        <>
          CHILE
          <br />
          SEGURO
        </>
      ),
    },
    {
      align: 'center',
      canSort: true,
      dataIndex: 'REP',
      key: 'REP',
      render: ({ votos }) => (
        <VoteWrapper>
          <VoteOption1>
            <ListText>
              {selectedUnit === 'porcentaje'
                ? percentFormatter.format(votos.totales ? Number(votos[2] / votos.totales) || 0 : 0)
                : normalNumberFormatter.format(Number(votos[2]) || 0)}
            </ListText>
          </VoteOption1>
          <VoteOption2>
            <ListVotesItem
              circumscriptionVotes={votos.totales}
              list="2"
              listVotes={votos[2]}
            />
          </VoteOption2>
        </VoteWrapper>
      ),
      sorter: variableListSorter(2, selectedUnit),
      title: (
        <>
          PARTIDO
          <br />
          REPUBLICANO
        </>
      ),
    },
  ]

  return tableColumns
}

const ImageWrapper = styled.div``

const CircumscriptionText = styled.div`
  font-weight: 500;
  cursor: pointer;
  a,
  a:hover,
  a:visited,
  a:active {
    color: inherit;
    text-decoration: underline;
  }
`

const ListText = styled.div`
  font-weight: 500;
  color: rgba(0, 0, 0, 0.85);
`

const RegionWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-items: center;
  gap: 10px;
  @media (max-width: ${spaces.tabletBreakpoint}) {
    ${ImageWrapper} {
      display: none;
    }
  }
`

const VoteWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  gap: ${spaces.s};
`

const VoteOption1 = styled.div`
  display: flex;
  justify-content: end;
  width: 50%;
  min-width: min-content;
`
const VoteOption2 = styled.div`
  display: flex;
  min-width: min-content;
`

export default getTableColumns
